import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Paperclip, X, FileText, FileCode, FileJson, Image } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Get the appropriate icon for a file based on its extension/type */
function getFileIcon(filename: string, mimeType?: string) {
  if (mimeType?.startsWith('image/')) {
    return Image;
  }

  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'json':
      return FileJson;
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
    case 'html':
    case 'css':
    case 'py':
    case 'sh':
    case 'sql':
    case 'yaml':
    case 'yml':
      return FileCode;
    default:
      return FileText;
  }
}

interface FileAttachmentButtonProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
  accept?: string;
  multiple?: boolean;
  className?: string;
}

/** Just the paperclip button for selecting files */
export function FileAttachmentButton({
  onFileSelect,
  disabled = false,
  accept,
  multiple = false,
  className
}: FileAttachmentButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        onFileSelect(file);
      });
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        accept={accept}
        multiple={multiple}
        className="hidden"
        disabled={disabled}
      />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleButtonClick}
        disabled={disabled}
        className="h-8 w-8 p-0 rounded-full hover:bg-muted/50 dark:hover:text-sidebar-primary"
        aria-label="Attach files"
        data-testid="paperclip-button"
      >
        <Paperclip className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface FileAttachmentListProps {
  files: File[];
  onFileRemove?: (file: File) => void;
  className?: string;
}

/** Horizontal list of attached files with preview on click */
export function FileAttachmentList({
  files,
  onFileRemove,
  className
}: FileAttachmentListProps) {
  const [previewFile, setPreviewFile] = useState<{ file: File; content: string } | null>(null);

  const handleRemoveFile = (file: File, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFileRemove) {
      onFileRemove(file);
    }
  };

  const handleFileClick = async (file: File) => {
    // Only show preview for text files, not images
    if (file.type.startsWith('image/')) return;

    try {
      const content = await file.text();
      setPreviewFile({ file, content });
    } catch {
      // If we can't read the file, don't show preview
    }
  };

  if (files.length === 0) return null;

  return (
    <>
      <div className={cn("flex flex-wrap gap-1.5", className)}>
        {files.map((file, index) => {
          const IconComponent = getFileIcon(file.name, file.type);
          const isClickable = !file.type.startsWith('image/');

          return (
            <div
              key={`${file.name}-${index}`}
              onClick={() => isClickable && handleFileClick(file)}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 bg-muted/60 rounded-lg text-xs border border-border/50",
                isClickable && "cursor-pointer hover:bg-muted hover:border-border transition-colors"
              )}
            >
              <IconComponent className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="truncate max-w-28 font-medium" title={file.name}>
                {file.name}
              </span>
              {onFileRemove && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleRemoveFile(file, e)}
                  className="h-4 w-4 p-0 hover:bg-muted-foreground/20 rounded-full ml-0.5"
                >
                  <X className="h-2.5 w-2.5" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={(open) => !open && setPreviewFile(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewFile && (() => {
                const IconComponent = getFileIcon(previewFile.file.name);
                return <IconComponent className="h-4 w-4" />;
              })()}
              {previewFile?.file.name}
            </DialogTitle>
            <DialogDescription>
              {previewFile && `${previewFile.content.length.toLocaleString()} characters · ${previewFile.content.split('\n').length.toLocaleString()} lines`}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <pre className="text-xs font-mono bg-muted/50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap break-words">
              {previewFile?.content}
            </pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface FileAttachmentProps {
  onFileSelect: (file: File) => void;
  onFileRemove?: (file: File) => void;
  selectedFiles?: File[];
  disabled?: boolean;
  accept?: string;
  multiple?: boolean;
  className?: string;
}

/** Combined file attachment button and list (legacy component for backward compatibility) */
export function FileAttachment({
  onFileSelect,
  onFileRemove,
  selectedFiles = [],
  disabled = false,
  accept,
  multiple = false,
  className
}: FileAttachmentProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <FileAttachmentButton
        onFileSelect={onFileSelect}
        disabled={disabled}
        accept={accept}
        multiple={multiple}
      />
      <FileAttachmentList
        files={selectedFiles}
        onFileRemove={onFileRemove}
      />
    </div>
  );
}